import React, { useState, useEffect, useRef } from 'react';
import { Clock, BookOpen, Check, Sparkles, ArrowLeft, Lightbulb, X } from 'lucide-react';
import { saveJournalEntry } from '../shared/api/journal';
import { updateSession } from '../shared/api/sessions';
import { countWords } from '../shared/utils/validation';
import { clearResumeData } from '../shared/utils/storage';
import { getTechnique } from '../shared/config/techniqueConfig';

const JournalingSession = ({ 
  journeyData, 
  userId, 
  sessionId,
  technique,
  onComplete 
}) => {
  const [step, setStep] = useState('setup');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [journalContent, setJournalContent] = useState('');
  const [timerActive, setTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimer = useRef(null);
  const intervalRef = useRef(null);

  const journalPrompts = {
    confidence: [
      "What would your most confident self do in this situation?",
      "Describe a moment when you felt truly powerful and capable.",
      "What beliefs about yourself are you ready to embrace?",
      "How will your life transform as you step into your confidence?"
    ],
    love: [
      "How can I show more love to myself today?",
      "What relationships in my life bring me joy and growth?",
      "What does my heart truly desire in love?",
      "How can I be more open to receiving love?"
    ],
    abundance: [
      "What does true abundance look and feel like in my life?",
      "What limiting beliefs about money am I ready to release?",
      "How will I use my abundance to create positive impact?",
      "What actions can I take today to invite more abundance?"
    ],
    healing: [
      "What am I releasing and leaving behind today?",
      "How does my healed, whole self feel?",
      "What gentle practices will support my healing journey?",
      "What wisdom has this experience taught me?"
    ],
    gratitude: [
      "What am I deeply grateful for right now?",
      "How has gratitude already transformed my perspective?",
      "What small blessings have I been overlooking?",
      "How can I cultivate more gratitude daily?"
    ],
    focus: [
      "What is my most important priority right now?",
      "What distractions am I ready to eliminate?",
      "What does my ideal focused, productive day look like?",
      "How will achieving this goal transform my life?"
    ]
  };

  const timerOptions = [
    { duration: 300, label: '5 min', description: 'Quick reflection' },
    { duration: 600, label: '10 min', description: 'Deep dive' },
    { duration: 900, label: '15 min', description: 'Full exploration' }
  ];

  const getCurrentPrompts = () => {
    if (technique && technique.journalPrompts) {
      console.log('✅ Using technique-specific prompts:', technique.name);
      return technique.journalPrompts;
    }
    
    const category = journeyData.category?.toLowerCase() || 'confidence';
    return journalPrompts[category] || journalPrompts.confidence;
  };

  const getRecommendedTimer = () => {
    if (technique && technique.timerRecommendation) {
      return technique.timerRecommendation;
    }
    return 600;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getWordCount = () => {
    return countWords(journalContent);
  };

  const startJournaling = (duration) => {
    setSelectedDuration(duration);
    setTimeLeft(duration);
    setStep('writing');
    setTimerActive(true);
  };

  useEffect(() => {
    if (timerActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timerActive, timeLeft]);

  useEffect(() => {
    if (journalContent.trim() && step === 'writing') {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }

      autoSaveTimer.current = setTimeout(() => {
        saveEntry(false);
      }, 2000);
    }

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [journalContent]);

  const saveEntry = async (isComplete = false) => {
    if (!journalContent.trim()) return;

    setIsSaving(true);

    try {
      const { entry, error } = await saveJournalEntry({
        user_id: userId,
        session_id: sessionId,
        content: journalContent,
        word_count: getWordCount(),
        technique_id: technique?.id || null
      });

      if (error) throw error;

      setLastSaved(new Date());
      console.log('✅ Journal entry saved');
    } catch (error) {
      console.error('❌ Error saving journal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompleteJournaling = async () => {
    console.log('✅ Journaling completed');
    await saveEntry(true);
    
    try {
      console.log('📍 Moving to meditation step');
      
      const { session, error } = await updateSession(sessionId, {
        current_step: 'meditation'
      });
      
      if (error) {
        console.error('❌ Error updating session step:', error);
      } else {
        console.log('✅ Session step updated to: meditation');
      }
    } catch (error) {
      console.error('❌ Error in handleCompleteJournaling:', error);
    }
    
    setShowCompletion(true);
    setTimeout(() => {
      setShowCompletion(false);
      onComplete();
    }, 2000);
  };

  if (step === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              {technique ? (
                <span className="text-3xl">{technique.icon}</span>
              ) : (
                <BookOpen className="w-10 h-10 text-white" />
              )}
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              {technique ? technique.name : 'Journaling Time'} ✍️
            </h1>
            <p className="text-gray-600 text-lg">
              {technique 
                ? technique.description 
                : `Reflect on your journey toward ${journeyData.title.toLowerCase()}`}
            </p>
          </div>

          {technique && technique.instructions && (
            <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-6 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Lightbulb className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-gray-800 font-bold text-lg mb-2">How This Works</h3>
                  <p className="text-gray-600 leading-relaxed">{technique.instructions}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Set Your Timer</h2>
            <p className="text-gray-600 text-center mb-8">Choose how long you'd like to write</p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {timerOptions.map((option) => {
                const isRecommended = option.duration === getRecommendedTimer();
                return (
                  <button
                    key={option.duration}
                    onClick={() => startJournaling(option.duration)}
                    className={`group relative bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-lg ${
                      isRecommended 
                        ? 'border-purple-500 ring-2 ring-purple-300' 
                        : 'border-purple-200 hover:border-purple-500'
                    }`}
                  >
                    {isRecommended && (
                      <div className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">
                        ⭐ Recommended
                      </div>
                    )}
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Clock className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-800 mb-2">
                      {option.label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {option.description}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="text-center">
              <p className="text-gray-500 text-sm mb-4">Or start writing without a timer</p>
              <button
                onClick={() => {
                  setStep('writing');
                  setSelectedDuration(null);
                }}
                className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold"
              >
                <BookOpen className="w-5 h-5" />
                Free Writing
              </button>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  console.log('📌 Saving session to continue later...');
                  clearResumeData();
                  sessionStorage.removeItem('selectedJourneyId');
                  window.location.href = '/';
                }}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
              >
                Continue Later →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {showCompletion && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-gray-800 text-2xl font-bold mb-2">
              {technique ? `${technique.name} Complete!` : 'Journaling Complete!'} 🎉
            </h2>
            <p className="text-gray-600">
              Your insights have been saved!
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setStep('setup')}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                {technique ? (
                  <span className="text-lg">{technique.icon}</span>
                ) : (
                  <Sparkles className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-gray-800 font-bold text-lg">
                  {technique ? technique.name : 'Journaling Session'}
                </h2>
                <p className="text-gray-500 text-sm">{journeyData.title}</p>
              </div>
            </div>
          </div>

          {selectedDuration && (
            <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-full">
              <Clock className="w-4 h-4 text-purple-600" />
              <span className={`font-mono font-semibold ${timeLeft < 60 ? 'text-orange-500' : 'text-purple-600'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-gray-700 font-semibold">
                  <BookOpen className="w-5 h-5" />
                  {technique ? `${technique.name} Practice` : 'Your Manifestation Journal'}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500">{getWordCount()} words</span>
                  {isSaving && <span className="text-purple-600">Saving...</span>}
                  {lastSaved && !isSaving && <span className="text-green-600">Saved</span>}
                </div>
              </div>

              <textarea
                value={journalContent}
                onChange={(e) => setJournalContent(e.target.value)}
                placeholder={technique 
                  ? `Begin practicing the ${technique.name}...` 
                  : "Begin writing your thoughts..."}
                className="w-full h-[500px] bg-gray-50 border border-gray-200 rounded-xl p-6 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none text-lg leading-relaxed"
              />

              <div className="mt-4 flex items-center justify-between">
                <span className="text-gray-400 text-sm">Auto-saves every 2 seconds</span>
                <button
                  onClick={handleCompleteJournaling}
                  disabled={!journalContent.trim()}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-8 rounded-full hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Meditation 🧘‍♀️
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="text-gray-800 font-bold text-lg mb-2">
                {technique ? 'Guided Steps' : 'Reflection Prompts'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {technique ? 'Follow these steps' : 'Use these to guide your writing'}
              </p>

              <div className="space-y-4">
                {getCurrentPrompts().map((prompt, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-purple-50 transition-colors cursor-pointer"
                    onClick={() => {
                      if (journalContent.trim() && !journalContent.endsWith('\n\n')) {
                        setJournalContent(prev => prev + '\n\n' + prompt + '\n');
                      } else {
                        setJournalContent(prev => prev + prompt + '\n');
                      }
                    }}
                  >
                    <p className="text-gray-700 text-sm leading-relaxed">{prompt}</p>
                  </div>
                ))}
              </div>

              {selectedDuration && timeLeft === 0 && (
                <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-green-700 font-semibold text-sm text-center">⏰ Timer Complete!</p>
                </div>
              )}

              {technique && technique.bestFor && (
                <div className="mt-6 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-purple-900 font-semibold text-sm mb-2">💡 Best For:</p>
                  <div className="flex flex-wrap gap-2">
                    {technique.bestFor.map((item, idx) => (
                      <span key={idx} className="bg-purple-200 text-purple-800 text-xs px-3 py-1 rounded-full capitalize">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalingSession;